import { useCallback, useEffect, useMemo, useRef, useState, type FormEvent, type ReactNode } from 'react';
import { MATIX_LOGO_DATA_URI } from '../login/logo';
import { SISMATIX_MARK_MASK_URI } from '../login/sismatix-logo';
import { Icon, type IconName } from './icons';
import { T, fill, type Bi } from './strings';
import {
  SetupApiError,
  fileToLogoDataUrl,
  setupApi,
  type SetupBusinessType,
  type SetupCompletePayload,
  type SetupStateResponse,
} from './setupApi';

/**
 * The Initial Setup Wizard — shown by every staff app (admin, cashier,
 * service, appointments) when sign-in answers `SETUP_REQUIRED` for a newly
 * provisioned tenant. The server issues no session until it is finished, so
 * there is nothing to bypass here: this component only collects answers and
 * hands them to POST /api/v1/setup/complete.
 *
 * Self-contained on purpose — its own plain-fetch client and its own
 * framework-free stylesheet (`@matix/ui/setup-styles`, `.swz-*`) — because the
 * hosting apps share neither an API client nor a CSS pipeline.
 *
 * Which modules a business type offers is NOT hard-coded here: it arrives in
 * `/state` from the server's business profile registry, so a fourth business
 * type needs no change to this file beyond its card copy.
 */

export type SetupLang = 'en' | 'ar';

export interface SetupOwnerCredentials {
  username: string;
  password: string;
}

export interface SetupWizardProps {
  tenantSlug: string;
  tenantName: string;
  /** Issued by the login route to the owner; null → the owner signs in here first. */
  setupToken: string | null;
  lang: SetupLang;
  onLangChange: (lang: SetupLang) => void;
  /**
   * Called from the final screen — the host app re-runs its normal sign-in.
   * `owner` is set when the owner signed in on the wizard's own form: those
   * are the credentials that worked, and the ones the login page held did not.
   */
  onComplete: (owner?: SetupOwnerCredentials) => void;
  /** Leave the wizard (back to the login page). */
  onExit: () => void;
  /** API origin; '' (same origin, via the dev proxy / IIS rewrite) by default. */
  apiBase?: string;
  /** The final button's label, e.g. "Open the till" in the cashier. */
  continueLabel?: Bi;
  supportEmail?: string;
  websiteUrl?: string;
}

const STEP_KEYS = ['welcome', 'type', 'info', 'branch', 'features', 'review', 'done'] as const;
type StepKey = (typeof STEP_KEYS)[number];

interface Answers {
  businessType: SetupBusinessType | null;
  nameAr: string;
  logo: string | null;
  country: string;
  currency: string;
  phone: string;
  email: string;
  addressEn: string;
  addressAr: string;
  vatEnabled: boolean;
  vatRate: string;
  vatIncluded: boolean;
  vatNumber: string;
  crNumber: string;
  branchName: string;
  branchNameAr: string;
  branchCity: string;
  branchStreet: string;
  branchBuilding: string;
  branchPhone: string;
  modules: string[];
  takeaway: boolean;
  delivery: boolean;
  pickup: boolean;
  dineIn: boolean;
  tablesCount: string;
  productImages: boolean;
  fullBarcodeScan: boolean;
  combineQtyOnScan: boolean;
  blockNegativeStock: boolean;
  staffOnLines: boolean;
}

interface Draft {
  v: 1;
  step: number;
  answers: Answers;
}

const BUSINESS_CARDS: Array<{ key: SetupBusinessType; icon: IconName; title: Bi; desc: Bi }> = [
  { key: 'restaurant', icon: 'restaurant', title: T.type.restaurant, desc: T.type.restaurantDesc },
  { key: 'retail', icon: 'retail', title: T.type.retail, desc: T.type.retailDesc },
  { key: 'salon', icon: 'salon', title: T.type.salon, desc: T.type.salonDesc },
  { key: 'laundry', icon: 'laundry', title: T.type.laundry, desc: T.type.laundryDesc },
];

const MODULE_COPY: Record<string, { title: Bi; desc: Bi; icon: IconName }> = {
  tables: { title: T.modules.tables, desc: T.modules.tablesDesc, icon: 'tables' },
  kitchen: { title: T.modules.kitchen, desc: T.modules.kitchenDesc, icon: 'kitchen' },
  delivery: { title: T.modules.delivery, desc: T.modules.deliveryDesc, icon: 'delivery' },
  callCenter: { title: T.modules.callCenter, desc: T.modules.callCenterDesc, icon: 'callCenter' },
  externalOrders: { title: T.modules.externalOrders, desc: T.modules.externalOrdersDesc, icon: 'externalOrders' },
  shipping: { title: T.modules.shipping, desc: T.modules.shippingDesc, icon: 'shipping' },
  inventory: { title: T.modules.inventory, desc: T.modules.inventoryDesc, icon: 'inventory' },
  appointments: { title: T.modules.appointments, desc: T.modules.appointmentsDesc, icon: 'appointments' },
  onlineBooking: { title: T.modules.onlineBooking, desc: T.modules.onlineBookingDesc, icon: 'onlineBooking' },
  qrMenu: { title: T.modules.qrMenu, desc: T.modules.qrMenuDesc, icon: 'qrMenu' },
  qrView: { title: T.modules.qrView, desc: T.modules.qrViewDesc, icon: 'qrMenu' },
  barcode: { title: T.modules.barcode, desc: T.modules.barcodeDesc, icon: 'barcode' },
  salesStaff: { title: T.modules.salesStaff, desc: T.modules.salesStaffDesc, icon: 'salesStaff' },
  laundry: { title: T.modules.laundry, desc: T.modules.laundryDesc, icon: 'laundry' },
};

function initialAnswers(state: SetupStateResponse): Answers {
  const c = state.current;
  const country = c.country_code ?? state.reference.countries[0]?.code ?? '';
  const currency =
    c.currency_code ?? state.reference.countries.find((x) => x.code === country)?.currency ?? '';
  return {
    businessType: null,
    nameAr: c.name_ar ?? '',
    logo: c.logo,
    country,
    currency,
    phone: '',
    email: '',
    addressEn: '',
    addressAr: '',
    vatEnabled: false,
    vatRate: country === 'SA' ? '15' : '5',
    vatIncluded: true,
    vatNumber: '',
    crNumber: '',
    branchName: c.branch?.name ?? 'Main Branch',
    branchNameAr: c.branch?.name_ar ?? 'الفرع الرئيسي',
    branchCity: c.branch?.city ?? '',
    branchStreet: c.branch?.street ?? '',
    branchBuilding: c.branch?.building ?? '',
    branchPhone: '',
    modules: [],
    takeaway: true,
    delivery: true,
    pickup: false,
    dineIn: true,
    tablesCount: '10',
    productImages: true,
    fullBarcodeScan: false,
    combineQtyOnScan: true,
    blockNegativeStock: false,
    staffOnLines: true,
  };
}

/** Answers from the last completed run (the ProfileJson the server kept). */
function answersFromPrevious(base: Answers, raw: unknown): Answers {
  if (!raw || typeof raw !== 'object') return base;
  const p = raw as Partial<SetupCompletePayload>;
  const b = p.business;
  const br = p.branch;
  const o = p.options;
  const str = (v: unknown, d: string) => (typeof v === 'string' ? v : d);
  const bool = (v: unknown, d: boolean) => (typeof v === 'boolean' ? v : d);
  return {
    ...base,
    businessType: p.business_type ?? base.businessType,
    modules: Array.isArray(p.modules) ? p.modules.filter((m): m is string => typeof m === 'string') : base.modules,
    nameAr: str(b?.name_ar, base.nameAr),
    country: str(b?.country_code, base.country),
    currency: base.currency || str(b?.currency_code, ''),
    phone: str(b?.phone, base.phone),
    email: str(b?.email, base.email),
    addressEn: str(b?.address_en, base.addressEn),
    addressAr: str(b?.address_ar, base.addressAr),
    vatEnabled: bool(b?.vat?.enabled, base.vatEnabled),
    vatRate: b?.vat?.rate != null ? String(b.vat.rate) : base.vatRate,
    vatIncluded: bool(b?.vat?.prices_include_vat, base.vatIncluded),
    vatNumber: str(b?.vat?.vat_number, base.vatNumber),
    crNumber: str(b?.vat?.commercial_no, base.crNumber),
    branchName: str(br?.name, base.branchName),
    branchNameAr: str(br?.name_ar, base.branchNameAr),
    branchCity: str(br?.city, base.branchCity),
    branchStreet: str(br?.street, base.branchStreet),
    branchBuilding: str(br?.building, base.branchBuilding),
    branchPhone: str(br?.phone, base.branchPhone),
    takeaway: bool(o?.takeaway, base.takeaway),
    delivery: bool(o?.delivery, base.delivery),
    pickup: bool(o?.pickup, base.pickup),
    dineIn: bool(o?.dine_in, base.dineIn),
    // Tables are only ever added to an empty branch, so a re-run offers none.
    tablesCount: '0',
    productImages: bool(o?.product_images, base.productImages),
    fullBarcodeScan: bool(o?.full_barcode_scan, base.fullBarcodeScan),
    combineQtyOnScan: bool(o?.combine_qty_on_scan, base.combineQtyOnScan),
    blockNegativeStock: bool(o?.block_negative_stock, base.blockNegativeStock),
  };
}

function isDraft(v: unknown): v is Draft {
  return typeof v === 'object' && v !== null && (v as Draft).v === 1 && typeof (v as Draft).answers === 'object';
}

function errorText(code: string, lang: SetupLang): string {
  return (T.errors[code] ?? T.errors.DEFAULT!)[lang];
}

const nz = (v: string): string | null => (v.trim() ? v.trim() : null);

function toPayload(a: Answers): SetupCompletePayload {
  return {
    business_type: a.businessType!,
    business: {
      name_ar: nz(a.nameAr),
      logo: a.logo,
      country_code: a.country,
      currency_code: a.currency,
      phone: nz(a.phone),
      email: nz(a.email),
      address_en: nz(a.addressEn),
      address_ar: nz(a.addressAr),
      vat: a.vatEnabled
        ? {
            enabled: true,
            rate: Number.parseInt(a.vatRate, 10) || 0,
            prices_include_vat: a.vatIncluded,
            vat_number: nz(a.vatNumber),
            commercial_no: nz(a.crNumber),
          }
        : null,
    },
    branch: {
      name: a.branchName.trim(),
      name_ar: nz(a.branchNameAr),
      city: nz(a.branchCity),
      street: nz(a.branchStreet),
      building: nz(a.branchBuilding),
      phone: nz(a.branchPhone),
    },
    modules: a.modules,
    options: {
      takeaway: a.takeaway,
      delivery: a.delivery && a.modules.includes('delivery'),
      pickup: a.pickup,
      dine_in: a.dineIn && a.modules.includes('tables'),
      tables_count: a.modules.includes('tables') ? Math.max(0, Math.min(300, Number.parseInt(a.tablesCount, 10) || 0)) : 0,
      product_images: a.productImages,
      full_barcode_scan: a.fullBarcodeScan,
      combine_qty_on_scan: a.combineQtyOnScan,
      block_negative_stock: a.blockNegativeStock,
      staff_on_lines: a.modules.includes('salesStaff'),
    },
  };
}

// ──────────────────────────────────────────────────────────────── pieces

function Brand({ lang }: { lang: SetupLang }): JSX.Element {
  return (
    <div className="swz-brand">
      <span
        className="swz-brand-mark"
        style={{ ['--swz-mark' as string]: `url(${SISMATIX_MARK_MASK_URI})` }}
        aria-hidden="true"
      />
      <span className="swz-brand-text">
        <span className="swz-brand-name">SISMATIX</span>
        <span className="swz-brand-tag">{T.brandTag[lang]}</span>
      </span>
    </div>
  );
}

function Field(props: {
  label: string;
  hint?: string;
  error?: string | null;
  optional?: boolean;
  lang: SetupLang;
  children: ReactNode;
  wide?: boolean;
}): JSX.Element {
  return (
    <label className={`swz-field${props.wide ? ' wide' : ''}${props.error ? ' has-error' : ''}`}>
      <span className="swz-label">
        {props.label}
        {props.optional && <em className="swz-optional">{T.optional[props.lang]}</em>}
      </span>
      {props.children}
      {props.error ? (
        <span className="swz-error-text" role="alert">
          {props.error}
        </span>
      ) : props.hint ? (
        <span className="swz-hint">{props.hint}</span>
      ) : null}
    </label>
  );
}

function Toggle(props: {
  checked: boolean;
  onChange: (v: boolean) => void;
  label: string;
  disabled?: boolean;
  note?: string | undefined;
}): JSX.Element {
  return (
    <label className={`swz-toggle${props.disabled ? ' disabled' : ''}`}>
      <input
        type="checkbox"
        checked={props.checked}
        disabled={props.disabled}
        onChange={(e) => props.onChange(e.target.checked)}
      />
      <span className="swz-switch" aria-hidden="true" />
      <span className="swz-toggle-label">
        {props.label}
        {props.note && <small>{props.note}</small>}
      </span>
    </label>
  );
}

/**
 * The owner's username / password, which the browser must NOT fill in.
 *
 * A browser that remembers the till's or the admin's login drops it into any
 * username + password pair it sees. Here that is worse than unhelpful: the
 * account on file is usually not the owner's, and whatever the owner then
 * types is APPENDED to the filled-in text, so correct credentials fail.
 * `autocomplete="off"` alone is ignored for login-looking fields, so the
 * field also starts read-only (browsers never autofill a read-only input)
 * and becomes editable on focus, with a name no password manager recognises.
 */
function NoAutofillInput(p: {
  value: string;
  onChange: (v: string) => void;
  secret?: boolean;
  autoFocus?: boolean;
}): JSX.Element {
  const [armed, setArmed] = useState(false);
  return (
    <input
      className="swz-input"
      type={p.secret ? 'password' : 'text'}
      name={p.secret ? 'swz-owner-key' : 'swz-owner-id'}
      autoComplete={p.secret ? 'new-password' : 'off'}
      autoCapitalize="none"
      autoCorrect="off"
      spellCheck={false}
      data-lpignore="true"
      data-1p-ignore="true"
      data-form-type="other"
      readOnly={!armed}
      autoFocus={p.autoFocus}
      onFocus={() => setArmed(true)}
      value={p.value}
      onChange={(e) => p.onChange(e.target.value)}
    />
  );
}

function ReviewSection(p: {
  title: string;
  step: StepKey;
  lang: SetupLang;
  onEdit: (step: StepKey) => void;
  children: ReactNode;
}): JSX.Element {
  return (
    <div className="swz-review-block">
      <div className="swz-review-head">
        <h3>{p.title}</h3>
        <button type="button" className="swz-link" onClick={() => p.onEdit(p.step)}>
          {T.edit[p.lang]}
        </button>
      </div>
      {p.children}
    </div>
  );
}

// ──────────────────────────────────────────────────────────────── wizard

export function SetupWizard(props: SetupWizardProps): JSX.Element {
  const {
    lang,
    onLangChange,
    tenantSlug,
    apiBase = '',
    supportEmail = 'support@matix.one',
    websiteUrl = 'https://sismatix.com',
  } = props;
  const dir = lang === 'ar' ? 'rtl' : 'ltr';

  const [token, setToken] = useState<string | null>(props.setupToken);
  const [phase, setPhase] = useState<'owner' | 'loading' | 'error' | 'wizard'>(
    props.setupToken ? 'loading' : 'owner',
  );
  const [loadError, setLoadError] = useState<string | null>(null);
  const [state, setState] = useState<SetupStateResponse | null>(null);
  const [answers, setAnswers] = useState<Answers | null>(null);
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [warnings, setWarnings] = useState<string[]>([]);
  const [logoError, setLogoError] = useState(false);
  const mainRef = useRef<HTMLElement>(null);

  const tenantName = state?.tenant_name ?? props.tenantName;
  const reconfiguring = state?.mode === 'reconfigure';

  // ── load
  const load = useCallback(
    async (t: string) => {
      setPhase('loading');
      setLoadError(null);
      try {
        const st = await setupApi.state(apiBase, t);
        setState(st);
        if (isDraft(st.draft)) {
          setAnswers({ ...initialAnswers(st), ...st.draft.answers });
          setStep(Math.min(Math.max(0, st.draft.step), STEP_KEYS.indexOf('review')));
        } else {
          const base = initialAnswers(st);
          setAnswers(
            st.previous
              ? answersFromPrevious(base, st.previous)
              : { ...base, businessType: st.business_type ?? null, ...(st.mode === 'reconfigure' ? { tablesCount: '0' } : {}) },
          );
          setStep(0);
        }
        setPhase('wizard');
      } catch (err) {
        const code = err instanceof SetupApiError ? err.code : 'DEFAULT';
        setLoadError(code);
        setPhase(code === 'INVALID_TOKEN' ? 'owner' : 'error');
      }
    },
    [apiBase],
  );

  useEffect(() => {
    if (token) void load(token);
  }, [token, load]);

  useEffect(() => {
    mainRef.current?.scrollTo?.({ top: 0 });
  }, [step]);

  const typeDef = useMemo(
    () => state?.reference.business_types.find((b) => b.key === answers?.businessType) ?? null,
    [state, answers?.businessType],
  );
  const offered = useMemo(
    () => (typeDef ? [...typeDef.default_modules, ...typeDef.optional_modules] : []),
    [typeDef],
  );

  const set = (patch: Partial<Answers>): void => {
    setAnswers((a) => (a ? { ...a, ...patch } : a));
    if (Object.keys(errors).length) setErrors({});
  };

  const persist = (nextStep: number, a: Answers): void => {
    if (!token || reconfiguring) return;
    const draft: Draft = { v: 1, step: nextStep, answers: a };
    void setupApi.saveDraft(apiBase, token, draft).catch(() => undefined);
  };

  // ── validation, one step at a time
  function validate(key: StepKey, a: Answers): Record<string, string> {
    const e: Record<string, string> = {};
    if (key === 'type' && !a.businessType) e.type = T.type.pickOne[lang];
    if (key === 'info') {
      if (!a.country) e.country = T.info.errCountry[lang];
      if (!a.currency) e.currency = T.info.errCurrency[lang];
      if (a.email.trim() && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(a.email.trim())) e.email = T.info.errEmail[lang];
      if (a.vatEnabled) {
        const r = Number.parseInt(a.vatRate, 10);
        if (!Number.isFinite(r) || r < 1 || r > 100) e.vatRate = T.info.errRate[lang];
      }
    }
    if (key === 'branch' && !a.branchName.trim()) e.branchName = T.branch.errName[lang];
    if (key === 'features') {
      const anyType =
        a.takeaway ||
        a.pickup ||
        (a.delivery && a.modules.includes('delivery')) ||
        (a.dineIn && a.modules.includes('tables'));
      if (!anyType) e.orderTypes = T.features.oneOrderType[lang];
    }
    return e;
  }

  const goNext = (): void => {
    if (!answers) return;
    const key = STEP_KEYS[step]!;
    const e = validate(key, answers);
    setErrors(e);
    if (Object.keys(e).length) return;
    const next = Math.min(step + 1, STEP_KEYS.length - 1);
    setStep(next);
    persist(next, answers);
  };

  const goBack = (): void => setStep((s) => Math.max(0, s - 1));
  const goTo = (key: StepKey): void => setStep(STEP_KEYS.indexOf(key));

  const chooseType = (key: SetupBusinessType): void => {
    if (!answers || !state) return;
    if (answers.businessType === key) return;
    const def = state.reference.business_types.find((b) => b.key === key);
    set({
      businessType: key,
      modules: def ? [...def.default_modules] : [],
      // A retail till scans; the other two mostly tap tiles.
      fullBarcodeScan: key === 'retail',
    });
  };

  const toggleModule = (m: string, on: boolean): void => {
    if (!answers) return;
    set({ modules: on ? [...new Set([...answers.modules, m])] : answers.modules.filter((x) => x !== m) });
  };

  const finish = async (): Promise<void> => {
    if (!answers || !token) return;
    setBusy(true);
    setSubmitError(null);
    try {
      const r = await setupApi.complete(apiBase, token, toPayload(answers));
      setWarnings(r.warnings ?? []);
      setStep(STEP_KEYS.indexOf('done'));
    } catch (err) {
      const code = err instanceof SetupApiError ? err.code : 'DEFAULT';
      if (code === 'SETUP_NOT_PENDING') {
        setStep(STEP_KEYS.indexOf('done'));
      } else {
        setSubmitError(errorText(code, lang));
      }
    } finally {
      setBusy(false);
    }
  };

  const onLogo = async (file: File | undefined): Promise<void> => {
    if (!file) return;
    setLogoError(false);
    try {
      set({ logo: await fileToLogoDataUrl(file, 512) });
    } catch {
      setLogoError(true);
    }
  };

  // ── owner sign-in (credentials on the login page were not the owner's)
  const [ownerUser, setOwnerUser] = useState('');
  const [ownerPass, setOwnerPass] = useState('');
  const [ownerBusy, setOwnerBusy] = useState(false);
  const [ownerError, setOwnerError] = useState<string | null>(null);
  const [ownerCreds, setOwnerCreds] = useState<SetupOwnerCredentials | undefined>(undefined);

  const ownerSignIn = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setOwnerBusy(true);
    setOwnerError(null);
    try {
      const creds = { username: ownerUser.trim(), password: ownerPass.replace(/\s+$/, '') };
      const r = await setupApi.auth(apiBase, tenantSlug, creds.username, creds.password);
      setOwnerCreds(creds);
      setToken(r.setup_token);
    } catch (err) {
      setOwnerError(errorText(err instanceof SetupApiError ? err.code : 'DEFAULT', lang));
      setOwnerPass('');
    } finally {
      setOwnerBusy(false);
    }
  };

  // ── chrome
  const stepKey = STEP_KEYS[step]!;
  const visibleSteps = STEP_KEYS;

  const langToggle = (
    <div className="swz-lang" role="group" aria-label="Language">
      <button type="button" aria-pressed={lang === 'ar'} onClick={() => onLangChange('ar')}>
        ع
      </button>
      <button type="button" aria-pressed={lang === 'en'} onClick={() => onLangChange('en')}>
        EN
      </button>
    </div>
  );

  const rail = (
    <aside className="swz-rail">
      <Brand lang={lang} />
      {phase === 'wizard' && (
        <ol className="swz-steps">
          {visibleSteps.map((k, i) => (
            <li key={k} className={i < step ? 'done' : i === step ? 'current' : ''} aria-current={i === step ? 'step' : undefined}>
              <span className="swz-step-dot">{i < step ? <Icon.check size={14} /> : i + 1}</span>
              <span className="swz-step-name">{T.steps[k][lang]}</span>
            </li>
          ))}
        </ol>
      )}
      <div className="swz-rail-foot">
        <a href={websiteUrl} target="_blank" rel="noreferrer noopener">
          <Icon.globe size={16} /> {T.website[lang]}
        </a>
        <a href={`mailto:${supportEmail}`}>
          <Icon.mail size={16} /> {supportEmail}
        </a>
        <img className="swz-product" src={MATIX_LOGO_DATA_URI} alt="Matix POS" />
      </div>
    </aside>
  );

  const progress = phase === 'wizard' && stepKey !== 'done' && (
    <div className="swz-progress" aria-hidden="true">
      <span style={{ width: `${((step + 1) / visibleSteps.length) * 100}%` }} />
    </div>
  );

  let body: ReactNode = null;

  if (phase === 'owner') {
    body = (
      <section className="swz-card narrow">
        <div className="swz-hero-icon">
          <Icon.lock size={30} />
        </div>
        <h1 className="swz-title">{T.owner.title[lang]}</h1>
        <p className="swz-lead">{T.owner.lead[lang]}</p>
        <p className="swz-muted">{T.owner.ask[lang]}</p>
        {loadError === 'INVALID_TOKEN' && <div className="swz-notice error">{errorText('INVALID_TOKEN', lang)}</div>}
        <form className="swz-form" autoComplete="off" onSubmit={(e) => void ownerSignIn(e)}>
          <Field label={T.owner.username[lang]} lang={lang} wide>
            <NoAutofillInput value={ownerUser} onChange={setOwnerUser} autoFocus />
          </Field>
          <Field label={T.owner.password[lang]} lang={lang} wide>
            <NoAutofillInput value={ownerPass} onChange={setOwnerPass} secret />
          </Field>
          {ownerError && (
            <div className="swz-notice error" role="alert">
              {ownerError}
            </div>
          )}
          <button className="swz-btn primary block" type="submit" disabled={ownerBusy || !ownerUser.trim() || !ownerPass}>
            {ownerBusy ? <span className="swz-spinner" /> : null}
            {T.owner.signIn[lang]}
          </button>
        </form>
        <p className="swz-muted small">{T.owner.notOwner[lang]}</p>
      </section>
    );
  } else if (phase === 'loading') {
    body = (
      <section className="swz-card narrow center" aria-busy="true">
        <span className="swz-spinner large" />
        <p className="swz-muted">{T.loading[lang]}</p>
      </section>
    );
  } else if (phase === 'error') {
    body = (
      <section className="swz-card narrow center">
        <div className="swz-notice error">{errorText(loadError ?? 'DEFAULT', lang)}</div>
        {loadError === 'SETUP_NOT_PENDING' ? (
          <button className="swz-btn primary" type="button" onClick={() => props.onComplete(ownerCreds)}>
            {(props.continueLabel ?? T.done.go)[lang]}
          </button>
        ) : (
          <button className="swz-btn primary" type="button" onClick={() => token && void load(token)}>
            {T.retry[lang]}
          </button>
        )}
      </section>
    );
  } else if (answers && state) {
    const a = answers;
    const countries = state.reference.countries;
    const currencies = state.reference.currencies;

    if (stepKey === 'welcome') {
      body = (
        <section className="swz-card hero">
          <div className="swz-welcome-mark" style={{ ['--swz-mark' as string]: `url(${SISMATIX_MARK_MASK_URI})` }} aria-hidden="true" />
          <h1 className="swz-display">
            {reconfiguring ? T.reconfigure.hi[lang] : fill(T.welcome.hi[lang], { name: tenantName })}
          </h1>
          <p className="swz-lead strong">{(reconfiguring ? T.reconfigure.lead : T.welcome.lead)[lang]}</p>
          <p className="swz-lead">{(reconfiguring ? T.reconfigure.body : T.welcome.body)[lang]}</p>
          <ul className="swz-points">
            {T.welcome.points.map((p, i) => (
              <li key={i}>
                <span className="swz-point-num">{i + 1}</span>
                {p[lang]}
              </li>
            ))}
          </ul>
          <button className="swz-btn primary large" type="button" onClick={goNext}>
            {(reconfiguring ? T.reconfigure.start : T.welcome.start)[lang]}
            <span className="swz-flip">
              <Icon.arrow size={18} />
            </span>
          </button>
        </section>
      );
    } else if (stepKey === 'type') {
      body = (
        <section className="swz-card">
          <h1 className="swz-title">{T.type.title[lang]}</h1>
          <p className="swz-lead">{T.type.lead[lang]}</p>
          <div className="swz-types" role="radiogroup" aria-label={T.type.title[lang]}>
            {BUSINESS_CARDS.filter((c) => state.reference.business_types.some((b) => b.key === c.key)).map((c) => {
              const IconC = Icon[c.icon];
              const selected = a.businessType === c.key;
              return (
                <button
                  key={c.key}
                  type="button"
                  role="radio"
                  aria-checked={selected}
                  className={`swz-type${selected ? ' selected' : ''}`}
                  onClick={() => chooseType(c.key)}
                >
                  <span className="swz-type-icon">
                    <IconC size={30} />
                  </span>
                  <span className="swz-type-title">{c.title[lang]}</span>
                  <span className="swz-type-desc">{c.desc[lang]}</span>
                  <span className="swz-type-check" aria-hidden="true">
                    <Icon.check size={16} />
                  </span>
                </button>
              );
            })}
          </div>
          {errors.type && (
            <div className="swz-notice error" role="alert">
              {errors.type}
            </div>
          )}
        </section>
      );
    } else if (stepKey === 'info') {
      body = (
        <section className="swz-card">
          <h1 className="swz-title">{T.info.title[lang]}</h1>
          <p className="swz-lead">{T.info.lead[lang]}</p>

          <div className="swz-grid">
            <Field label={T.info.name[lang]} hint={T.info.nameHint[lang]} lang={lang}>
              <input className="swz-input" autoComplete="off" value={tenantName} readOnly aria-readonly="true" />
            </Field>
            <Field label={T.info.nameAr[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="rtl" value={a.nameAr} onChange={(e) => set({ nameAr: e.target.value })} maxLength={200} />
            </Field>

            <Field label={T.info.logo[lang]} hint={T.info.logoHint[lang]} optional lang={lang} wide error={logoError ? T.info.logoError[lang] : null}>
              <div className="swz-logo">
                <div className="swz-logo-preview">
                  {a.logo ? <img src={a.logo} alt="" /> : <Icon.upload size={26} />}
                </div>
                <div className="swz-logo-actions">
                  <span className="swz-btn ghost">
                    <input
                      type="file"
                      accept="image/png,image/jpeg,image/webp"
                      onChange={(e) => {
                        void onLogo(e.target.files?.[0]);
                        e.target.value = '';
                      }}
                    />
                    {a.logo ? T.info.replace[lang] : T.info.upload[lang]}
                  </span>
                  {a.logo && (
                    <button type="button" className="swz-link" onClick={() => set({ logo: null })}>
                      {T.info.remove[lang]}
                    </button>
                  )}
                </div>
              </div>
            </Field>

            <Field label={T.info.country[lang]} lang={lang} error={errors.country}>
              <select
                className="swz-input" autoComplete="off"
                value={a.country}
                onChange={(e) => {
                  const code = e.target.value;
                  const cur = countries.find((c) => c.code === code)?.currency;
                  set({
                    country: code,
                    ...(cur && currencies.some((c) => c.code === cur) ? { currency: cur } : {}),
                    vatRate: code === 'SA' ? '15' : a.vatRate,
                  });
                }}
              >
                {countries.map((c) => (
                  <option key={c.code} value={c.code}>
                    {lang === 'ar' ? c.name_ar : c.name}
                  </option>
                ))}
              </select>
            </Field>
            <Field label={T.info.currency[lang]} hint={T.info.currencyHint[lang]} lang={lang} error={errors.currency}>
              <select className="swz-input" autoComplete="off" value={a.currency} onChange={(e) => set({ currency: e.target.value })}>
                {!a.currency && <option value="" />}
                {currencies.map((c) => (
                  <option key={c.code} value={c.code}>
                    {c.code} — {lang === 'ar' ? c.name_ar : c.name}
                  </option>
                ))}
              </select>
            </Field>
          </div>

          <h2 className="swz-subtitle">{T.info.contact[lang]}</h2>
          <div className="swz-grid">
            <Field label={T.info.phone[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="ltr" inputMode="tel" value={a.phone} onChange={(e) => set({ phone: e.target.value })} maxLength={40} />
            </Field>
            <Field label={T.info.email[lang]} optional lang={lang} error={errors.email}>
              <input className="swz-input" autoComplete="off" dir="ltr" type="email" value={a.email} onChange={(e) => set({ email: e.target.value })} maxLength={200} />
            </Field>
            <Field label={T.info.address[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="ltr" value={a.addressEn} onChange={(e) => set({ addressEn: e.target.value })} maxLength={400} />
            </Field>
            <Field label={T.info.addressAr[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="rtl" value={a.addressAr} onChange={(e) => set({ addressAr: e.target.value })} maxLength={400} />
            </Field>
          </div>

          <h2 className="swz-subtitle">
            {T.info.vat[lang]} <em className="swz-optional">{T.optional[lang]}</em>
          </h2>
          <Toggle checked={a.vatEnabled} onChange={(v) => set({ vatEnabled: v })} label={T.info.vatToggle[lang]} />
          {a.vatEnabled && (
            <div className="swz-grid swz-indent">
              <Field label={T.info.vatRate[lang]} lang={lang} error={errors.vatRate}>
                <input
                  className="swz-input" autoComplete="off"
                  dir="ltr"
                  inputMode="numeric"
                  value={a.vatRate}
                  onChange={(e) => set({ vatRate: e.target.value.replace(/[^\d]/g, '').slice(0, 3) })}
                />
              </Field>
              <div className="swz-field">
                <span className="swz-label">&nbsp;</span>
                <Toggle checked={a.vatIncluded} onChange={(v) => set({ vatIncluded: v })} label={T.info.vatIncluded[lang]} />
              </div>
              <Field label={T.info.vatNumber[lang]} optional lang={lang}>
                <input className="swz-input" autoComplete="off" dir="ltr" value={a.vatNumber} onChange={(e) => set({ vatNumber: e.target.value })} maxLength={50} />
              </Field>
              <Field label={T.info.crNumber[lang]} optional lang={lang}>
                <input className="swz-input" autoComplete="off" dir="ltr" value={a.crNumber} onChange={(e) => set({ crNumber: e.target.value })} maxLength={50} />
              </Field>
            </div>
          )}
        </section>
      );
    } else if (stepKey === 'branch') {
      body = (
        <section className="swz-card">
          <h1 className="swz-title">{T.branch.title[lang]}</h1>
          <p className="swz-lead">{T.branch.lead[lang]}</p>
          <div className="swz-grid">
            <Field label={T.branch.name[lang]} lang={lang} error={errors.branchName}>
              <input className="swz-input" autoComplete="off" value={a.branchName} onChange={(e) => set({ branchName: e.target.value })} maxLength={100} />
            </Field>
            <Field label={T.branch.nameAr[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="rtl" value={a.branchNameAr} onChange={(e) => set({ branchNameAr: e.target.value })} maxLength={100} />
            </Field>
            <Field label={T.branch.city[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" value={a.branchCity} onChange={(e) => set({ branchCity: e.target.value })} maxLength={100} />
            </Field>
            <Field label={T.branch.street[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" value={a.branchStreet} onChange={(e) => set({ branchStreet: e.target.value })} maxLength={200} />
            </Field>
            <Field label={T.branch.building[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" value={a.branchBuilding} onChange={(e) => set({ branchBuilding: e.target.value })} maxLength={50} />
            </Field>
            <Field label={T.branch.phone[lang]} optional lang={lang}>
              <input className="swz-input" autoComplete="off" dir="ltr" inputMode="tel" value={a.branchPhone} onChange={(e) => set({ branchPhone: e.target.value })} maxLength={40} />
            </Field>
          </div>
          <p className="swz-muted small">{T.branch.moreLater[lang]}</p>
        </section>
      );
    } else if (stepKey === 'features') {
      const on = (m: string) => a.modules.includes(m);
      const type = a.businessType;
      body = (
        <section className="swz-card">
          <h1 className="swz-title">{T.features.title[lang]}</h1>
          <p className="swz-lead">{T.features.lead[lang]}</p>

          <h2 className="swz-subtitle">{T.features.modules[lang]}</h2>
          <div className="swz-modules">
            {offered.map((m) => {
              const copy = MODULE_COPY[m];
              if (!copy) return null;
              const IconC = Icon[copy.icon];
              return (
                <label key={m} className={`swz-module${on(m) ? ' on' : ''}`}>
                  <input type="checkbox" checked={on(m)} onChange={(e) => toggleModule(m, e.target.checked)} />
                  <span className="swz-module-icon">
                    <IconC size={22} />
                  </span>
                  <span className="swz-module-text">
                    <strong>{copy.title[lang]}</strong>
                    <small>{copy.desc[lang]}</small>
                  </span>
                  <span className="swz-switch" aria-hidden="true" />
                </label>
              );
            })}
          </div>

          {on('tables') && (
            <Field label={T.features.tablesCount[lang]} hint={T.features.tablesCountHint[lang]} lang={lang}>
              <input
                className="swz-input short" autoComplete="off"
                dir="ltr"
                inputMode="numeric"
                value={a.tablesCount}
                onChange={(e) => set({ tablesCount: e.target.value.replace(/[^\d]/g, '').slice(0, 3) })}
              />
            </Field>
          )}

          <h2 className="swz-subtitle">{T.features.orderTypes[lang]}</h2>
          <div className="swz-chips">
            <Toggle checked={a.takeaway} onChange={(v) => set({ takeaway: v })} label={T.features.takeaway[lang]} />
            {(type === 'restaurant' || offered.includes('delivery')) && (
              <Toggle
                checked={a.delivery && on('delivery')}
                disabled={!on('delivery')}
                note={!on('delivery') ? T.features.needsDelivery[lang] : undefined}
                onChange={(v) => set({ delivery: v })}
                label={T.features.delivery[lang]}
              />
            )}
            <Toggle checked={a.pickup} onChange={(v) => set({ pickup: v })} label={T.features.pickup[lang]} />
            {offered.includes('tables') && (
              <Toggle
                checked={a.dineIn && on('tables')}
                disabled={!on('tables')}
                note={!on('tables') ? T.features.needsTables[lang] : undefined}
                onChange={(v) => set({ dineIn: v })}
                label={T.features.dineIn[lang]}
              />
            )}
          </div>
          {errors.orderTypes && (
            <div className="swz-notice error" role="alert">
              {errors.orderTypes}
            </div>
          )}

          <h2 className="swz-subtitle">{T.features.preferences[lang]}</h2>
          <div className="swz-stack">
            <Toggle checked={a.productImages} onChange={(v) => set({ productImages: v })} label={T.features.productImages[lang]} />
            {on('barcode') && (
              <>
                <Toggle checked={a.fullBarcodeScan} onChange={(v) => set({ fullBarcodeScan: v })} label={T.features.fullBarcode[lang]} />
                <Toggle checked={a.combineQtyOnScan} onChange={(v) => set({ combineQtyOnScan: v })} label={T.features.combineQty[lang]} />
              </>
            )}
            {on('inventory') && (
              <Toggle checked={a.blockNegativeStock} onChange={(v) => set({ blockNegativeStock: v })} label={T.features.blockNegative[lang]} />
            )}
          </div>

          <div className="swz-included">
            <strong>{T.features.included[lang]}</strong>
            <span>{T.features.includedList[lang]}</span>
          </div>
        </section>
      );
    } else if (stepKey === 'review') {
      const typeCard = BUSINESS_CARDS.find((c) => c.key === a.businessType);
      const country = countries.find((c) => c.code === a.country);
      const orderTypes = [
        a.takeaway && T.features.takeaway[lang],
        a.delivery && a.modules.includes('delivery') && T.features.delivery[lang],
        a.pickup && T.features.pickup[lang],
        a.dineIn && a.modules.includes('tables') && T.features.dineIn[lang],
      ].filter(Boolean) as string[];
      const tables = a.modules.includes('tables') ? Number.parseInt(a.tablesCount, 10) || 0 : 0;
      body = (
        <section className="swz-card">
          <h1 className="swz-title">{T.review.title[lang]}</h1>
          <p className="swz-lead">{T.review.lead[lang]}</p>
          {reconfiguring && <div className="swz-notice">{T.reconfigure.warn[lang]}</div>}
          <div className="swz-review">
            <ReviewSection lang={lang} onEdit={goTo} title={T.review.businessType[lang]} step="type">
              <p className="swz-review-value">{typeCard?.title[lang]}</p>
            </ReviewSection>
            <ReviewSection lang={lang} onEdit={goTo} title={T.review.business[lang]} step="info">
              <div className="swz-review-business">
                {a.logo && <img src={a.logo} alt="" />}
                <div>
                  <p className="swz-review-value">{tenantName}</p>
                  {a.nameAr && <p dir="rtl">{a.nameAr}</p>}
                  <p className="swz-muted">
                    {(lang === 'ar' ? country?.name_ar : country?.name) ?? a.country} · {a.currency}
                  </p>
                  <p className="swz-muted">
                    {a.vatEnabled ? fill(T.review.vatOn[lang], { rate: a.vatRate }) : T.review.vatOff[lang]}
                  </p>
                </div>
              </div>
            </ReviewSection>
            <ReviewSection lang={lang} onEdit={goTo} title={T.review.branch[lang]} step="branch">
              <p className="swz-review-value">{lang === 'ar' && a.branchNameAr ? a.branchNameAr : a.branchName}</p>
              {[a.branchStreet, a.branchCity].some(Boolean) && (
                <p className="swz-muted">{[a.branchBuilding, a.branchStreet, a.branchCity].filter(Boolean).join(', ')}</p>
              )}
            </ReviewSection>
            <ReviewSection lang={lang} onEdit={goTo} title={T.review.enabled[lang]} step="features">
              <ul className="swz-ticks">
                {a.modules.map((m) => (
                  <li key={m}>
                    <Icon.check size={16} /> {MODULE_COPY[m]?.title[lang] ?? m}
                    {m === 'tables' && tables > 0 ? ` — ${fill(T.review.tables[lang], { n: tables })}` : ''}
                  </li>
                ))}
                {orderTypes.map((o) => (
                  <li key={o}>
                    <Icon.check size={16} /> {o}
                  </li>
                ))}
                <li>
                  <Icon.check size={16} /> {T.review.core[lang]}
                </li>
              </ul>
            </ReviewSection>
          </div>
          {submitError && (
            <div className="swz-notice error" role="alert">
              {submitError}
            </div>
          )}
        </section>
      );
    } else {
      body = (
        <section className="swz-card hero center">
          <div className="swz-success" aria-hidden="true">
            <Icon.check size={44} />
          </div>
          <h1 className="swz-display">{(reconfiguring ? T.reconfigure.doneTitle : T.done.title)[lang]}</h1>
          <p className="swz-lead">{(reconfiguring ? T.reconfigure.doneLead : T.done.lead)[lang]}</p>
          {warnings.length > 0 && (
            <div className="swz-notice">
              <strong>{T.done.warnings[lang]}</strong>
              <ul>
                {warnings.map((w) => (
                  <li key={w}>{T.warn[w]?.[lang] ?? w}</li>
                ))}
              </ul>
            </div>
          )}
          <button className="swz-btn primary large" type="button" onClick={() => props.onComplete(ownerCreds)}>
            {(props.continueLabel ?? T.done.go)[lang]}
            <span className="swz-flip">
              <Icon.arrow size={18} />
            </span>
          </button>
        </section>
      );
    }
  }

  const showNav = phase === 'wizard' && stepKey !== 'welcome' && stepKey !== 'done';

  return (
    <div className="swz-root" dir={dir} lang={lang}>
      {rail}
      <main className="swz-main" ref={mainRef}>
        <header className="swz-top">
          <div className="swz-top-brand">
            <Brand lang={lang} />
          </div>
          {phase === 'wizard' && stepKey !== 'done' && (
            <span className="swz-stepof">
              {fill(T.stepOf[lang], { n: step + 1, total: visibleSteps.length })}
              <b>{T.steps[stepKey][lang]}</b>
            </span>
          )}
          <div className="swz-top-actions">
            {langToggle}
            {stepKey !== 'done' && (
              <button type="button" className="swz-link" onClick={props.onExit}>
                {(reconfiguring ? T.reconfigure.cancel : T.exit)[lang]}
              </button>
            )}
          </div>
        </header>
        {progress}
        <div className="swz-body">{body}</div>
        {showNav && (
          <footer className="swz-nav">
            <button type="button" className="swz-btn ghost" onClick={goBack} disabled={busy}>
              <span className="swz-flip back">
                <Icon.arrow size={18} />
              </span>
              {T.back[lang]}
            </button>
            {stepKey === 'review' ? (
              <button type="button" className="swz-btn primary" onClick={() => void finish()} disabled={busy}>
                {busy && <span className="swz-spinner" />}
                {busy ? T.review.applying[lang] : (reconfiguring ? T.reconfigure.save : T.review.finish)[lang]}
              </button>
            ) : (
              <button type="button" className="swz-btn primary" onClick={goNext}>
                {T.next[lang]}
                <span className="swz-flip">
                  <Icon.arrow size={18} />
                </span>
              </button>
            )}
          </footer>
        )}
      </main>
    </div>
  );
}
